/**
 * OrgPicker Component
 *
 * Dropdown that lets a **staff admin** choose which organization's members to
 * view and manage. Rendered only in staff mode (see UsersPage); the gate is the
 * `platform.staff.admin` role in the v1 claim.
 *
 * Data flow:
 * - Options come from {@link useOrgsStore} (`GET /api/tenant-management/orgs`),
 *   NOT from the claim — a global staff role can't enumerate orgs from token
 *   grants alone.
 * - The selected org is the single source of truth held by
 *   {@link useUsersStore} as `targetOrgId`; selecting one calls `setTargetOrg`,
 *   which scopes every subsequent member call to `?org=<id>` and reloads.
 *
 * @module components/OrgPicker
 */

import { useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
} from '@mui/material';
import { useOrgsStore } from '../stores/useOrgsStore';
import { useUsersStore } from '../stores/useUsersStore';
import { formatErrorMessage, orgOptionLabel } from '../utils';

// =============================================================================
// Constants
// =============================================================================

/**
 * Sentinel value for the unselected state. MUI's Select needs a defined,
 * non-null value; we map `targetOrgId === null` to this empty string and back.
 */
const UNSELECTED = '';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for the OrgPicker component.
 */
export interface OrgPickerProps {
  /**
   * Compact layout for embedding in a dense container (the TopBar). Drops the
   * page-level bottom margin so the control sits flush in a toolbar. Purely
   * presentational — the picker's behavior/state is unchanged.
   */
  compact?: boolean;
}

// =============================================================================
// OrgPicker Component
// =============================================================================

/**
 * OrgPicker - Organization selector for staff admins.
 *
 * Loads the selectable orgs on mount, renders them in a labelled dropdown, and
 * drives the users store's target org on change. Shows a spinner while the org
 * list loads and a retry alert if the load fails.
 */
export function OrgPicker({ compact = false }: OrgPickerProps = {}) {
  // Bottom margin only when standalone on a page; flush inside a toolbar.
  const blockMargin = compact ? 0 : 3;

  // Compact mode renders on the primary (blue) AppBar, where the default
  // light outlined field + black label/text clash. Re-skin the field
  // light-on-dark, keyed off the theme's `primary.contrastText` rather than a
  // hardcoded color, so contrast stays principled if the palette changes: a
  // faint translucent fill gives the field presence against the bar, and the
  // label, selected text, dropdown icon, and outline all adopt the contrast
  // color (dimmed at rest, brighter on hover/focus). Purely presentational —
  // behavior and state are untouched. Only applied in compact mode (`{}` keeps
  // the standalone page picker on the default light treatment).
  const compactFieldSx = compact
    ? {
        '& .MuiOutlinedInput-root': {
          color: 'primary.contrastText',
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
          borderRadius: 1,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.8)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.contrastText',
          },
        },
        '& .MuiSelect-icon': { color: 'primary.contrastText' },
        '& .MuiInputLabel-root': {
          color: 'rgba(255, 255, 255, 0.8)',
          '&.Mui-focused': { color: 'primary.contrastText' },
        },
      }
    : {};
  // Orgs list (picker options)
  const orgs = useOrgsStore((state) => state.orgs);
  const isLoading = useOrgsStore((state) => state.isLoading);
  const error = useOrgsStore((state) => state.error);
  const isLoaded = useOrgsStore((state) => state.isLoaded);
  const loadOrgs = useOrgsStore((state) => state.loadOrgs);
  const clearError = useOrgsStore((state) => state.clearError);

  // Selected target org (single source of truth in the users store)
  const targetOrgId = useUsersStore((state) => state.targetOrgId);
  const setTargetOrg = useUsersStore((state) => state.setTargetOrg);

  // Load the org list once, lazily. The orgs store is a singleton that persists
  // across navigation, but the TopBar (which now hosts this picker) remounts on
  // every page — so guard on `isLoaded` to avoid refetching `GET /orgs` (and
  // flashing the spinner) on each nav. The explicit Retry path still calls
  // `loadOrgs` directly after a failed load (where `isLoaded` is false).
  useEffect(() => {
    if (!isLoaded) {
      void loadOrgs();
    }
  }, [isLoaded, loadOrgs]);

  /**
   * Handle a selection change. An empty selection clears the target (falls back
   * to the active org); otherwise we scope to the chosen org id.
   */
  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    void setTargetOrg(value === UNSELECTED ? null : value);
  };

  /**
   * Retry loading the org list after a failure.
   */
  const handleRetry = () => {
    clearError();
    void loadOrgs();
  };

  // Error state: show a dismissible alert with retry. Honest failure — never
  // fall back to fixture data (the org list is real backend data only).
  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mb: blockMargin }}
        data-testid="org-picker-error"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={handleRetry}
            data-testid="org-picker-retry-button"
          >
            Retry
          </Button>
        }
      >
        {`Couldn't load organizations: ${formatErrorMessage(error)}`}
      </Alert>
    );
  }

  // Empty state: the load succeeded but the backend returned no orgs. Be
  // honest about it rather than rendering a silently-disabled picker.
  if (isLoaded && orgs.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: blockMargin }} data-testid="org-picker-empty">
        No organizations are available for you to manage.
      </Alert>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: blockMargin }}
      data-testid="org-picker"
    >
      <FormControl
        sx={{ minWidth: 280, ...compactFieldSx }}
        size="small"
        disabled={isLoading || orgs.length === 0}
      >
        {/* `shrink` keeps the label floated even in the empty state. With
            `displayEmpty` the placeholder is always visible, so without this the
            label would sit ON TOP of the "Select an organization…" placeholder
            (MUI label/placeholder overlap). The matching `notched` OutlinedInput
            cuts the outline gap so the floated label doesn't cross the border. */}
        <InputLabel id="org-picker-label" shrink>
          Organization
        </InputLabel>
        <Select
          labelId="org-picker-label"
          value={targetOrgId ?? UNSELECTED}
          onChange={handleChange}
          displayEmpty
          input={<OutlinedInput notched label="Organization" />}
          data-testid="org-picker-select"
          inputProps={{ 'data-testid': 'org-picker-input' }}
        >
          {/* Placeholder option — the staff admin must pick an org. */}
          <MenuItem value={UNSELECTED} data-testid="org-picker-placeholder">
            <em>Select an organization…</em>
          </MenuItem>
          {orgs.map((org) => (
            <MenuItem
              key={org.id}
              value={org.id}
              data-testid={`org-picker-option-${org.id}`}
            >
              {orgOptionLabel(org)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Loading indicator while the org list is being fetched. */}
      {isLoading && !isLoaded && (
        <CircularProgress size={20} data-testid="org-picker-loading" />
      )}
    </Box>
  );
}
